import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import httpClient from "@/common/api/httpClient";
import { useAuth } from "@/common/hooks/useAuth";

// Strict union types prevent silent typos like "TEST_CASE" vs "TEST_CASES"
export type PermissionResource =
  | "TEST_CASES"
  | "TEST_CYCLE"
  | "RESULTS"
  | "ENVIRONMENTS"
  | "TEST_DATA"
  | "TEST_DATA_SETS"
  | "ELEMENTS"
  | "DEFECTS"
  | "REQUIREMENTS"
  | "SETTINGS"
  | "ROLES"
  | "AGENTS"
  | "BACKUP"
  | "UPLOADS"
  | "TAGS"
  | "COMMENTS"
  | "USERS"
  | "PROJECTS"
  | "ANALYTICS"
  | "REPORTS"
  | "AI_TEST_GEN"
  | "INTEGRATIONS"
  | "SCHEDULER"
  | "NOTIFICATIONS"
  | "ATTACHMENTS"
  | "WORKSPACE_VERSIONS";

export type PermissionAction =
  | "READ"
  | "WRITE"
  | "DELETE"
  | "EXECUTE"
  | "EXPORT"
  | "IMPORT";

interface PermissionEntry {
  resource: PermissionResource;
  action: PermissionAction;
}

interface PermissionsResponse {
  isAdmin: boolean;
  isSuperAdmin?: boolean;
  noRoleAssigned?: boolean;
  // Normal project members are returned under `roleName`; the org-admin/super-admin
  // branches of the backend return the effective role under `role` instead.
  roleName?: string | null;
  role?: string | null;
  permissions: PermissionEntry[];
}

async function fetchMyPermissions(
  projectId: number,
): Promise<PermissionsResponse> {
  // No leading slash — basePath already ends with "/" so adding "/" would make "//roles/..."
  const res = await httpClient(
    "GET",
    `roles/me/permissions?projectId=${projectId}`,
  );
  return res.data;
}

/**
 * Returns permission helpers for the currently authenticated user in the active project.
 * Reads projectId reactively from AuthContext — re-queries automatically on project switch.
 *
 * Usage:
 *   const { can, isAdmin, isLoading } = usePermissions();
 *   if (can('TEST_CASES', 'WRITE')) { ... }
 */
export function usePermissions(overrideProjectId?: number | null) {
  const {
    user,
    userPreferenceData,
    isUserPreferenceLoading,
    isSuperAdmin: authIsSuperAdmin,
    isOrgAdmin: authIsOrgAdmin,
  } = useAuth();
  const preferenceProjectId: number | null =
    userPreferenceData?.data?.projectId ?? null;
  const projectId = overrideProjectId ?? preferenceProjectId;

  const {
    data,
    isLoading: isPermissionsLoading,
    isError,
  } = useQuery<PermissionsResponse>({
    queryKey: ["permissions", projectId],
    queryFn: () => fetchMyPermissions(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });

  const rawRole =
    (user as any)?.orgRoleName ||
    (user as any)?.roleName ||
    (user as any)?.role ||
    userPreferenceData?.data?.roleName ||
    data?.roleName ||
    data?.role ||
    "";
  const normalizedRole = String(rawRole).toLowerCase().trim();

  const isTestManager =
    normalizedRole === "test manager" ||
    normalizedRole === "org admin" ||
    normalizedRole === "organization admin" ||
    authIsSuperAdmin ||
    authIsOrgAdmin;

  // Still loading if: preferences haven't loaded yet OR permissions query is in flight
  const isLoading =
    isUserPreferenceLoading || (!!projectId && isPermissionsLoading);

  const isAdmin = !!(
    data?.isAdmin ||
    authIsSuperAdmin ||
    authIsOrgAdmin ||
    isTestManager
  );

  const can = useCallback(
    (resource: PermissionResource, action: PermissionAction): boolean => {
      // Super admins, org owners, and Test Managers always have full access even before
      // the permissions query fires (e.g. bare preference with no projectId) and across
      // all projects in the organization.
      if (authIsSuperAdmin || authIsOrgAdmin || isTestManager || isAdmin)
        return true;
      if (isLoading) return false;
      if (isError || !data) return false;
      return data.permissions.some(
        (p) => p.resource === resource && p.action === action,
      );
    },
    [
      data,
      isAdmin,
      isLoading,
      isError,
      authIsSuperAdmin,
      authIsOrgAdmin,
      isTestManager,
    ],
  );

  /** Checks ONLY the project-role permission rows — ignores org-admin bypass.
   *  Use when a feature must be restricted by project role even for org admins. */
  const canRole = useCallback(
    (resource: PermissionResource, action: PermissionAction): boolean => {
      if (authIsSuperAdmin || authIsOrgAdmin || isTestManager) return true;
      if (isLoading) return false;
      if (isError || !data) return false;
      return data.permissions.some(
        (p) => p.resource === resource && p.action === action,
      );
    },
    [data, isLoading, isError, authIsSuperAdmin, authIsOrgAdmin, isTestManager],
  );

  return {
    can,
    canRole,
    isAdmin,
    isLoading,
    roleName: isTestManager
      ? "Test Manager"
      : (data?.roleName ?? data?.role ?? (rawRole || null)),
  };
}
